import { CartAddEvent } from '@theme/events';

const DESKTOP_QUERY = '(min-width: 750px)';
const EDITOR_OPEN_KEY = 'lick-menu-editor-open';
/** Least space between the Shop All button and the desktop panel; it otherwise matches the space above the button. */
const PANEL_GAP = 12;
/** Longer than the desktop panel's exit transition in _header-mega-menu.liquid. */
const PANEL_EXIT_MS = 420;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Publishes a Shopify customer event (menu_*) for the analytics pixel in Settings > Customer events.
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
function track(name, data) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch {}
}

/**
 * The menu panel an element sits in (intro, product, links, promo), for click events.
 * @param {Element} element
 */
function menuArea(element) {
  const panel = element.closest('.lick-menu__panel');
  if (!panel) return element.closest('.lick-menu__footer') ? 'footer' : 'other';
  const panelClass = [...panel.classList].find((className) => className.startsWith('lick-menu-'));
  return panelClass ? panelClass.slice('lick-menu-'.length) : 'other';
}

/** The page scroll lock is shared; keep it while any mega menu is still open. */
function releaseScrollLock() {
  if (!document.querySelector('.lick-menu__dialog[open]')) {
    document.documentElement.classList.remove('lick-menu-locked');
  }
}

/**
 * Shop mega menu. One dialog serves both layouts: a non-modal panel under the header on desktop
 * and a modal drawer on mobile. Toggles anywhere in the document reference it via aria-controls.
 */
class LickMenu extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {HTMLElement | null} */
  opener = null;

  desktop = window.matchMedia(DESKTOP_QUERY);

  /** Set when another dialog takes over (cart, account), so closing does not pull focus back. */
  #handingOff = false;

  #headerObserver = new ResizeObserver(() => this.#position());

  /** @type {number | undefined} */
  #lowerTimer;

  /** Ends the running mobile section open/close animation at its final state. */
  #settleSection = () => {};

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('focusout', this.#onFocusOut);
    for (const toggle of this.toggles) {
      toggle.addEventListener('pointerenter', this.#warmImages, { once: true });
      toggle.addEventListener('focus', this.#warmImages, { once: true });
      toggle.addEventListener('touchstart', this.#warmImages, { once: true, passive: true });
    }
    this.dialog.addEventListener('close', this.#onClose);
    this.desktop.addEventListener('change', this.#onBreakpointChange);
    document.addEventListener('cart:update', this.#onCartUpdate);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
      // The editor re-renders the header after each setting change; reopen if a menu block is being edited.
      if (sessionStorage.getItem(EDITOR_OPEN_KEY) === this.dialog.id) this.open();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.dialog?.removeEventListener('close', this.#onClose);
    this.desktop.removeEventListener('change', this.#onBreakpointChange);
    window.removeEventListener('resize', this.#position);
    this.#headerObserver.disconnect();
    clearTimeout(this.#lowerTimer);
    this.#setRaised(false);
    // A re-render replaces this element while the drawer may be open; release the page scroll lock.
    releaseScrollLock();
  }

  get trigger() {
    return this.querySelector('.lick-menu__trigger');
  }

  get toggles() {
    return document.querySelectorAll(`[data-lick-menu-toggle][aria-controls="${this.dialog?.id}"]`);
  }

  /** @param {HTMLElement} [opener] */
  open(opener) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;

    this.opener = opener ?? null;
    this.#warmImages();

    if (this.desktop.matches) {
      // Measure first: the entrance starts from the offset, and show() resolves the starting style.
      this.#position();
      clearTimeout(this.#lowerTimer);
      this.#setRaised(true);
      // show() focuses the first link and scrolls the page to it while the panel is still above the screen.
      // Inert for that call, the panel takes no focus; the trigger keeps it so Tab walks into the menu.
      dialog.inert = true;
      dialog.show();
      dialog.inert = false;
      this.opener?.focus({ preventScroll: true });
      document.documentElement.classList.add('lick-menu-locked');
      window.addEventListener('resize', this.#position);
      const container = this.#containingBlock();
      if (container) this.#headerObserver.observe(container);
    } else {
      dialog.showModal();
      // Focus the drawer itself so screen readers announce it without a focus ring on the first control.
      dialog.focus({ preventScroll: true });
      document.documentElement.classList.add('lick-menu-locked');
    }

    this.#setExpanded(true);
    track('menu_opened', { layout: this.desktop.matches ? 'desktop' : 'mobile' });
  }

  close() {
    if (!this.dialog?.open) return;
    // The header may have changed height since opening; the exit has to clear the top of the screen.
    this.#position();
    this.dialog.close();
  }

  #onClose = () => {
    // The close event is queued; a later open (e.g. selecting the next block in the editor) wins.
    if (this.dialog?.open) return;
    if (window.Shopify?.designMode && sessionStorage.getItem(EDITOR_OPEN_KEY) === this.dialog?.id) {
      sessionStorage.removeItem(EDITOR_OPEN_KEY);
    }
    window.removeEventListener('resize', this.#position);
    this.#headerObserver.disconnect();
    releaseScrollLock();
    this.#setExpanded(false);
    // Stay above the announcement bar until the panel has left the screen.
    clearTimeout(this.#lowerTimer);
    this.#lowerTimer = setTimeout(() => {
      if (!this.dialog?.open) this.#setRaised(false);
    }, PANEL_EXIT_MS);

    if (this.#handingOff) {
      this.#handingOff = false;
      return;
    }

    const wasModal = !this.desktop.matches;
    if (wasModal || this.contains(document.activeElement) || document.activeElement === document.body) {
      this.opener?.focus({ preventScroll: true });
    }
  };

  /** Lazy images inside a closed dialog never load; start them as soon as a visitor reaches for the menu. */
  #warmImages = () => {
    for (const image of this.dialog?.querySelectorAll('img[loading="lazy"]') ?? []) image.loading = 'eager';
  };

  /** @param {boolean} raised */
  #setRaised(raised) {
    this.closest('.shopify-section')?.classList.toggle('lick-menu-raised', raised);
  }

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    for (const toggle of this.toggles) toggle.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * Opens or closes a mobile menu section. The menu column grows or shrinks to its new height, so
   * sections drop down open and fold up shut. A closing section stays visible until it is covered.
   * @param {HTMLElement} section
   */
  #toggleSection(section) {
    const grid = section.closest('.lick-menu__grid');
    const open = section.getAttribute('aria-expanded') !== 'true';
    const from = grid instanceof HTMLElement ? grid.offsetHeight : 0;
    this.#settleSection();
    section.setAttribute('aria-expanded', String(open));
    track('menu_section_toggled', {
      section: section.dataset.lickMenuSection || section.textContent?.trim().toLowerCase() || '',
      open,
    });
    if (!(grid instanceof HTMLElement) || window.matchMedia(REDUCED_MOTION_QUERY).matches) return;

    const to = grid.offsetHeight;
    section.toggleAttribute('data-lick-menu-closing', !open);
    // Shown rows would replay the drawer's sideways entrance; the height change is their motion.
    for (const animation of grid.getAnimations({ subtree: true })) {
      if (animation instanceof CSSAnimation) animation.finish();
    }

    grid.style.overflow = 'clip';
    const animation = grid.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: open ? 380 : 280, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
    );
    const settle = () => {
      if (this.#settleSection !== settle) return;
      this.#settleSection = () => {};
      animation.cancel();
      section.removeAttribute('data-lick-menu-closing');
      grid.style.overflow = '';
    };
    this.#settleSection = settle;
    animation.onfinish = settle;
  }

  /** Closes the menu because another dialog is opening; that dialog manages focus. */
  #handOff() {
    if (!this.dialog?.open) return;
    this.#handingOff = true;
    this.close();
  }

  /** The positioned ancestor the desktop panel sits in. offsetParent is null while the dialog is closed. */
  #containingBlock() {
    for (let element = this.dialog?.parentElement; element; element = element.parentElement) {
      const style = getComputedStyle(element);
      if (style.position !== 'static' || style.transform !== 'none' || style.translate !== 'none') return element;
    }
    return null;
  }

  /**
   * Places the desktop panel just under the button, caps its height to the viewport, and records how far
   * its top is from the top of the screen so it can enter from, and leave to, above the viewport.
   * The header shrinks when it sticks, so these are measured rather than fixed in CSS.
   */
  #position = () => {
    const dialog = this.dialog;
    const container = this.#containingBlock();
    const trigger = this.trigger?.getBoundingClientRect();
    if (!dialog || !container || !trigger || trigger.height === 0 || !this.desktop.matches) return;

    const headerTop = Math.max(this.closest('#header-component')?.getBoundingClientRect().top ?? 0, 0);
    const top = trigger.bottom + Math.max(PANEL_GAP, Math.round(trigger.top - headerTop));
    dialog.style.setProperty('--lick-top', `${Math.round(top - container.getBoundingClientRect().top)}px`);
    dialog.style.setProperty('--lick-offset', `${Math.round(Math.max(top, 0))}px`);
    dialog.style.setProperty('--lick-max-height', `${Math.round(Math.max(240, window.innerHeight - top - 16))}px`);
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const dialog = this.dialog;
    if (!dialog || !(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-lick-menu-toggle]');
    if (toggle instanceof HTMLElement && toggle.getAttribute('aria-controls') === dialog.id) {
      event.preventDefault();
      if (dialog.open) {
        this.close();
      } else {
        this.open(toggle);
      }
      return;
    }

    if (!dialog.open) return;

    // Clicking the modal backdrop lands on the dialog element itself.
    if (event.target === dialog) {
      this.close();
      return;
    }

    if (event.target.closest('[data-lick-menu-close]')) {
      this.close();
      return;
    }

    // Mobile section headings (Shop, Variety packs, ...) open and close their section in place.
    const section = event.target.closest('[data-lick-menu-section]');
    if (section instanceof HTMLElement && dialog.contains(section)) {
      this.#toggleSection(section);
      return;
    }

    const cartLink = event.target.closest('[data-lick-menu-cart]');
    const cartDrawer = this.#cartDrawer;
    if (cartLink && cartDrawer?.open) {
      event.preventDefault();
      this.#handOff();
      cartDrawer.open();
      return;
    }

    // Open the same Shopify account panel as the header icon instead of leaving the page.
    const accountLink = event.target.closest('[data-lick-menu-account]');
    const account = /** @type {HTMLElement & { showModal?: () => void } | null} */ (
      document.querySelector('shopify-account')
    );
    if (accountLink && typeof account?.showModal === 'function') {
      event.preventDefault();
      this.#handOff();
      account.showModal();
      return;
    }

    const link = event.target.closest('a[href]');
    if (link && dialog.contains(link)) {
      track('menu_link_clicked', {
        area: menuArea(link),
        label: link.textContent?.trim().replace(/\s+/g, ' ').slice(0, 80) ?? '',
        href: link.getAttribute('href') ?? '',
      });
      this.close();
      return;
    }

    if (!dialog.contains(event.target)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const dialog = this.dialog;
    if (!dialog) return;

    if (event.key === 'Escape' && dialog.open && !dialog.matches(':modal')) {
      this.close();
      return;
    }

    // The desktop trigger is a link with role="button"; Space should toggle it like a button.
    if (event.key === ' ' && event.target instanceof HTMLAnchorElement && event.target.matches('[data-lick-menu-toggle]')) {
      event.preventDefault();
      event.target.click();
    }
  };

  #onBreakpointChange = () => this.close();

  get #cartDrawer() {
    return /** @type {HTMLElement & { open?: () => void } | null} */ (document.querySelector('cart-drawer-component'));
  }

  /**
   * Tabbing past the end of the desktop panel closes it, like the theme's other header menus.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const dialog = this.dialog;
    if (!dialog?.open || !this.desktop.matches || window.Shopify?.designMode) return;

    const next = event.relatedTarget;
    if (!(next instanceof Node) || this.contains(next)) return;
    if (next instanceof Element && next.closest(`[data-lick-menu-toggle][aria-controls="${dialog.id}"]`)) return;

    this.#handOff();
  };

  /**
   * Quick add for products that need no choices. Without JavaScript the form posts to /cart/add.
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-lick-menu-add]')) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const error = form.querySelector('[data-lick-menu-add-error]');
    if (error) error.textContent = '';

    const variantId = String(new FormData(form).get('id') ?? '');
    const sections = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
          .filter(Boolean)
      ),
    ];

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url ?? `${form.action}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) throw new Error(data.description ?? data.message ?? '');
      track('menu_quick_add', { area: menuArea(form), variant_id: variantId });

      const cartDrawer = this.#cartDrawer;
      // Without a cart drawer, the cart page is the confirmation.
      if (!cartDrawer) {
        window.location.assign(window.Theme?.routes?.cart_url ?? '/cart');
        return;
      }

      this.#handOff();
      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: 1,
          variantId,
          sections: data.sections,
        })
      );
      // The drawer opens itself on add only when the theme's auto-open setting is on.
      if (cartDrawer?.open && !cartDrawer.hasAttribute('auto-open')) cartDrawer.open();
    } catch (cartError) {
      // Keep the menu open and announce Shopify's reason (for example, no more stock) in the alert region.
      if (error instanceof HTMLElement) {
        error.textContent =
          cartError instanceof Error && cartError.message ? cartError.message : error.dataset.fallback ?? '';
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  /**
   * Product forms report the quantity just added; cart line edits report the new total.
   * @param {Event & { detail?: { data?: { itemCount?: number, source?: string } } }} event
   */
  #onCartUpdate = (event) => {
    const data = event.detail?.data;
    if (typeof data?.itemCount !== 'number') return;

    for (const count of this.querySelectorAll('[data-lick-menu-cart-count]')) {
      const current = Number(count.textContent) || 0;
      count.textContent = String(data.source === 'product-form-component' ? current + data.itemCount : data.itemCount);
    }
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Node) || !this.#ownsEditorTarget(event.target)) return;
    if (this.dialog) sessionStorage.setItem(EDITOR_OPEN_KEY, this.dialog.id);
    // A block being edited stays in view: open every section.
    for (const section of this.querySelectorAll('[data-lick-menu-section]')) section.setAttribute('aria-expanded', 'true');
    this.open();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (!(event.target instanceof Node) || !this.#ownsEditorTarget(event.target)) return;
    if (sessionStorage.getItem(EDITOR_OPEN_KEY) === this.dialog?.id) sessionStorage.removeItem(EDITOR_OPEN_KEY);
    this.close();
  };

  /** @param {Node} target */
  #ownsEditorTarget(target) {
    return target === this || this.contains(target);
  }
}

if (!customElements.get('lick-menu')) customElements.define('lick-menu', LickMenu);

/**
 * Menu rows can link to `/?variant=<id>#buy-box-section`. The homepage buy box ignores the variant
 * parameter server-side, so select the matching flavor once the picker is ready.
 */
async function selectBuyBoxVariantFromUrl() {
  const variantId = new URLSearchParams(window.location.search).get('variant');
  if (!variantId || !/^\d+$/.test(variantId)) return;

  const match = document.querySelector(`#buy-box-section [data-variant-id="${variantId}"]`);
  await customElements.whenDefined('variant-picker');

  // Button pickers use radios; dropdown pickers use options inside a select.
  if (match instanceof HTMLInputElement && !match.checked) {
    match.click();
  } else if (match instanceof HTMLOptionElement && !match.selected) {
    const select = match.closest('select');
    if (!select) return;
    select.value = match.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

selectBuyBoxVariantFromUrl();
